import type { User } from "firebase/auth";
import { CHARACTER_ACTIONS } from "@/character/actions";
import { defaultGridTuning, gridTuningForImageSize, type GridTuning } from "@/character/draft";
import { readImageFileAsDataUrl } from "@/character/frame";
import {
  GRID_COLUMNS,
  GRID_ROWS,
  supportsTemplateGridLayout,
  TEMPLATE_FRAME_HEIGHT,
  TEMPLATE_FRAME_WIDTH,
} from "@/character/grid";
import { createDefaultHomeEconomy, type HomeEconomy } from "@/home/economy";
import { loadHomeEconomyFromIdb, saveHomeEconomyToIdb } from "@/home/home-idb";
import { HomeEvents } from "@/home/home-events";
import { downscaleSheetDataUrlIfOverscaled } from "@/character/sheet-scale-down";
import { mountSheetUploadPreview } from "@/character/sheet-upload-preview";
import {
  applySheetUploadClean,
  bindSheetUploadCleanControls,
  getSheetUploadCleanPrefs,
  renderSheetUploadCleanControlsHtml,
} from "@/character/sheet-upload-clean";
import {
  composePetFrameGrid,
  countFilledPetFrameCells,
  emptyPetFrameGrid,
  missingPetFrameCells,
  type PetFrameGrid,
} from "@/pet/compose-pet-frame-grid";
import { appendPetFromSpritesheet } from "@/pet/create-pet-from-sheet";
import { extractPetFrameGridFromSheet } from "@/pet/extract-pet-frame-grid-from-sheet";
import { equippedPet, loadPetState, savePetState } from "@/pet/firestore-storage";
import { openPetFrameCropModal } from "@/pet/mount-pet-frame-crop-modal";
import { syncEquippedPetPublic } from "@/pet/sync-equipped-pet-public";
import type { PetPlayerState } from "@/pet/types";
import {
  clearPetSheetGridCreateSession,
  notifyPetSheetGridCreated,
  notifyPetSheetGridOpener,
  readPetSheetGridCreateSession,
  savePetSheetGridResult,
} from "@/pet/pet-sheet-grid-result";

function escapeHtml(text: string): string {
  return text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function loadImageSize(dataUrl: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => reject(new Error("โหลดภาพไม่สำเร็จ"));
    img.src = dataUrl;
  });
}

export function mountPetSheetGridPage(root: HTMLElement, user: User): () => void {
  root.className = "page-shell page-shadow-grid";
  const createSession = readPetSheetGridCreateSession();
  const createMode =
    new URLSearchParams(window.location.search).get("create") === "1" ||
    createSession != null;
  let draftName = createSession?.draftName ?? "";
  let petState: PetPlayerState | null = null;
  let economy: HomeEconomy = createDefaultHomeEconomy();
  let economyReady = false;
  let cells: PetFrameGrid = emptyPetFrameGrid();
  let status = "";
  let error = "";
  let busy = false;
  let previewUrl: string | null = null;
  let unbindClean: (() => void) | undefined;
  let closeCropModal: (() => void) | undefined;
  let autoSheetSourceDataUrl: string | null = null;
  let autoSheetPreviewDataUrl: string | null = null;
  let autoGridTuning: GridTuning = defaultGridTuning();
  let stopAutoSheetPreview: (() => void) | undefined;
  let mountedAutoPreviewUrl: string | null = null;

  const renderGridHtml = () => {
    const rows = CHARACTER_ACTIONS
      .map((action) => {
        const cols = Array.from({ length: GRID_COLUMNS }, (_, col) => {
          const url = cells[action.row]?.[col];
          const filled = Boolean(url);
          return `<label class="shadow-frame-cell${filled ? " shadow-frame-cell--filled" : ""}" data-frame-row="${action.row}" data-frame-col="${col}">
            <input type="file" class="shadow-frame-file" accept="image/png,image/jpeg,image/webp" hidden />
            ${
              filled
                ? `<img class="shadow-frame-thumb" src="${url}" alt="" width="48" height="48" />`
                : `<span class="shadow-frame-placeholder" aria-hidden="true">+</span>`
            }
            <span class="shadow-frame-index">${col + 1}</span>
            ${
              filled
                ? `<button type="button" class="shadow-frame-clear" data-frame-clear="${action.row}-${col}" aria-label="ลบเฟรม">×</button>`
                : ""
            }
          </label>`;
        }).join("");
        return `<div class="shadow-frame-row">
          <div class="shadow-frame-row-label">
            <strong>${escapeHtml(action.templateLabel)}</strong>
            <span class="hint">${escapeHtml(action.label)}</span>
          </div>
          <div class="shadow-frame-row-cells">${cols}</div>
        </div>`;
      })
      .join("");

    return rows;
  };

  const render = () => {
    const filled = countFilledPetFrameCells(cells);
    const total = GRID_ROWS * GRID_COLUMNS;
    const gridComplete = filled >= total;
    const canCreateFromAutoSheet = Boolean(autoSheetPreviewDataUrl);
    const canCreate =
      createMode &&
      economyReady &&
      economy.petCreateCards > 0 &&
      (gridComplete || canCreateFromAutoSheet) &&
      !busy;
    const createBlockedReason =
      createMode && economyReady && economy.petCreateCards < 1
        ? "ไม่มีบัตรสร้างสัตว์เลี้ยง"
        : "";
    const createReadyHint = createMode
      ? canCreate
        ? gridComplete
          ? "พร้อมสร้างจากกริดด้านล่าง"
          : "พร้อมสร้างจากชีตที่จับกริดแล้ว (หรือดึงลงกริดก่อนก็ได้)"
        : !economyReady
          ? "กำลังโหลดข้อมูลผู้เล่น…"
          : createBlockedReason
            ? ""
            : !canCreateFromAutoSheet
              ? `กรอกเฟรมให้ครบ ${total} ช่อง หรืออัปโหลดชีตเต็มแผ่นแล้วจับกริด`
              : ""
      : "";
    const createBar = createMode
      ? `<div class="shadow-grid-create-bar" role="region" aria-label="สร้างสัตว์เลี้ยง">
          <button type="button" class="btn btn-primary btn-lg shadow-grid-create-btn" data-shadow-grid-create ${canCreate ? "" : "disabled"}>
            สร้างสัตว์เลี้ยง
          </button>
          ${createReadyHint ? `<p class="hint shadow-grid-create-bar-hint">${escapeHtml(createReadyHint)}</p>` : ""}
        </div>`
      : "";
    const title = createMode ? "สร้างสัตว์เลี้ยง · กริดเฟรม" : "อัปโหลดทีละเฟรม";
    const lede = createMode
      ? `กรอกครบ ${total} ช่อง แล้วกดสร้างสัตว์เลี้ยง — ใช้บัตรสร้าง 1 ใบ (คงเหลือ ${economyReady ? economy.petCreateCards : "…"})`
      : `${GRID_ROWS} แถว × ${GRID_COLUMNS} เฟรม · ขนาดเซลล์ ${TEMPLATE_FRAME_WIDTH}×${TEMPLATE_FRAME_HEIGHT}px · กรอกครบ ${total} ช่องก่อนส่งกลับ`;
    const nameField = createMode
      ? `<label class="field shadow-grid-create-name">
          <span class="field-label">ชื่อสัตว์เลี้ยง</span>
          <input type="text" class="input" id="shadow-grid-create-name" maxlength="48" value="${escapeHtml(draftName)}" ${busy ? "disabled" : ""} />
        </label>`
      : "";
    const primaryAction = createMode
      ? `<button type="button" class="btn btn-primary" data-shadow-grid-create ${canCreate ? "" : "disabled"}>
            สร้างสัตว์เลี้ยง
          </button>
          <button type="button" class="btn btn-ghost" id="shadow-grid-apply" ${busy || filled < total ? "disabled" : ""}>
            ส่งชีตกลับแท็บเดิม
          </button>`
      : `<button type="button" class="btn btn-primary" id="shadow-grid-apply" ${busy || filled < total ? "disabled" : ""}>
            ใช้ชีตนี้และกลับ
          </button>`;
    root.innerHTML = `
      <main class="auth-card character-card character-card--wide shadow-grid-page" aria-labelledby="shadow-grid-title">
        <p class="eyebrow">สัตว์เลี้ยง</p>
        <h1 id="shadow-grid-title">${title}</h1>
        <p class="lede hint">${lede}</p>
        ${createBlockedReason ? `<p class="form-error" role="alert">${escapeHtml(createBlockedReason)}</p>` : ""}
        ${nameField}
        ${createBar}
        <p class="hint">กรอกแล้ว <strong>${filled}</strong> / ${total}</p>
        <div id="shadow-grid-clean-host">${renderSheetUploadCleanControlsHtml("shadow-grid-clean")}</div>
        <section class="shadow-grid-auto-sheet" aria-labelledby="shadow-grid-auto-title">
          <h2 id="shadow-grid-auto-title" class="shadow-grid-auto-title">ชีตเต็มแผ่น · จับกริดอัตโนมัติ</h2>
          <p class="hint">อัปโหลดชีต 4×6 — ลากปรับกริดบนภาพ แล้วดึงลงช่องด้านล่าง (แทน Snap ทีละเฟรม)</p>
          <div class="home-actions shadow-grid-auto-actions">
            <label class="btn btn-ghost shadow-grid-auto-pick">
              เลือกไฟล์ชีต
              <input type="file" id="shadow-grid-full-sheet-file" accept="image/png,image/jpeg,image/webp" hidden />
            </label>
            <button type="button" class="btn btn-primary" id="shadow-grid-auto-apply" ${busy || !autoSheetPreviewDataUrl ? "disabled" : ""}>
              ดึงทุกเฟรมลงกริด
            </button>
            <button type="button" class="btn btn-ghost" id="shadow-grid-auto-clear" ${!autoSheetPreviewDataUrl || busy ? "disabled" : ""}>
              ปิดตัวอย่างชีต
            </button>
          </div>
          <div id="shadow-grid-auto-sheet-host" class="shadow-grid-auto-sheet-host" ${autoSheetPreviewDataUrl ? "" : "hidden"}></div>
        </section>
        <div class="shadow-frame-grid" role="group" aria-label="กริดเฟรมสัตว์เลี้ยง">
          ${renderGridHtml()}
        </div>
        ${
          previewUrl
            ? `<div class="shadow-grid-compose-preview">
            <p class="hint">ตัวอย่างชีตที่ประกอบแล้ว</p>
            <img class="shadow-grid-compose-img" src="${previewUrl}" alt="ชีตรวม" />
          </div>`
            : ""
        }
        ${error ? `<p class="form-error" role="alert">${escapeHtml(error)}</p>` : ""}
        ${status ? `<p class="hint" role="status">${escapeHtml(status)}</p>` : ""}
        <div class="home-actions shadow-grid-actions">
          ${primaryAction}
          <button type="button" class="btn btn-ghost" id="shadow-grid-preview" ${busy || filled < total ? "disabled" : ""}>
            ดูตัวอย่างชีตรวม
          </button>
          <button type="button" class="btn btn-ghost" id="shadow-grid-clear-all" ${busy ? "disabled" : ""}>ล้างทั้งหมด</button>
          <button type="button" class="btn btn-ghost" id="shadow-grid-close">ปิดแท็บ</button>
        </div>
      </main>`;

    unbindClean?.();
    const cleanHost = root.querySelector<HTMLElement>("#shadow-grid-clean-host");
    unbindClean = cleanHost ? bindSheetUploadCleanControls(cleanHost) : undefined;

    mountAutoSheetPreviewIfNeeded();
  };

  const mountAutoSheetPreviewIfNeeded = () => {
    const previewHost = root.querySelector<HTMLElement>("#shadow-grid-auto-sheet-host");
    if (!previewHost || !autoSheetPreviewDataUrl) {
      stopAutoSheetPreview?.();
      stopAutoSheetPreview = undefined;
      mountedAutoPreviewUrl = null;
      return;
    }
    previewHost.hidden = false;
    if (mountedAutoPreviewUrl === autoSheetPreviewDataUrl && stopAutoSheetPreview) {
      return;
    }
    stopAutoSheetPreview?.();
    mountedAutoPreviewUrl = autoSheetPreviewDataUrl;
    stopAutoSheetPreview = mountSheetUploadPreview(
      previewHost,
      autoSheetPreviewDataUrl,
      autoGridTuning,
      (tuning) => {
        autoGridTuning = tuning;
      },
      {
        sourceDataUrl: autoSheetSourceDataUrl ?? autoSheetPreviewDataUrl,
        onSheetDataUrlChange: (url) => {
          autoSheetPreviewDataUrl = url;
          mountedAutoPreviewUrl = url;
        },
        templateAlignMode: true,
      },
    );
  };

  const clearAutoSheetPreview = () => {
    stopAutoSheetPreview?.();
    stopAutoSheetPreview = undefined;
    mountedAutoPreviewUrl = null;
    autoSheetSourceDataUrl = null;
    autoSheetPreviewDataUrl = null;
    autoGridTuning = defaultGridTuning();
  };

  const ingestFullSheetFile = async (file: File) => {
    busy = true;
    error = "";
    status = "กำลังโหลดชีต…";
    render();
    try {
      let dataUrl = await readImageFileAsDataUrl(file);
      const downscaled = await downscaleSheetDataUrlIfOverscaled(dataUrl);
      dataUrl = downscaled.dataUrl;
      const probe = new Image();
      await new Promise<void>((resolve, reject) => {
        probe.onload = () => resolve();
        probe.onerror = () => reject(new Error("โหลดภาพไม่สำเร็จ"));
        probe.src = dataUrl;
      });
      autoSheetSourceDataUrl = dataUrl;
      autoGridTuning = gridTuningForImageSize(
        probe.naturalWidth,
        probe.naturalHeight,
        supportsTemplateGridLayout,
      );
      autoSheetPreviewDataUrl = await applySheetUploadClean(dataUrl, getSheetUploadCleanPrefs());
      const scaleNote =
        downscaled.appliedScale > 1 ? `ลดขนาด ${downscaled.appliedScale}× · ` : "";
      status = `${scaleNote}ลากกริดให้ตรงเซลล์ แล้วกด «ดึงทุกเฟรมลงกริด»`;
    } catch (e) {
      clearAutoSheetPreview();
      error = e instanceof Error ? e.message : "เปิดชีตไม่สำเร็จ";
      status = "";
    } finally {
      busy = false;
      render();
    }
  };

  const applyAutoSheetToGrid = async () => {
    if (!autoSheetPreviewDataUrl) return;
    busy = true;
    error = "";
    status = "กำลังแยกเฟรมจากกริด…";
    render();
    try {
      const extracted = await extractPetFrameGridFromSheet(
        autoSheetPreviewDataUrl,
        autoGridTuning,
        getSheetUploadCleanPrefs(),
      );
      cells = extracted;
      previewUrl = null;
      status = `ดึงครบ ${GRID_ROWS * GRID_COLUMNS} เฟรมแล้ว — ตรวจช่องด้านล่างหรือแก้ทีละช่อง`;
    } catch (e) {
      error = e instanceof Error ? e.message : "แยกเฟรมไม่สำเร็จ";
      status = "";
    } finally {
      busy = false;
      render();
    }
  };

  const setCell = (row: number, col: number, dataUrl: string | null) => {
    if (!cells[row]) return;
    cells[row][col] = dataUrl;
    previewUrl = null;
  };

  const actionLabelForRow = (row: number): string => {
    const action = CHARACTER_ACTIONS.find((a) => a.row === row);
    return action?.templateLabel ?? `แถว ${row + 1}`;
  };

  const saveCroppedFrameToCell = async (
    row: number,
    col: number,
    croppedDataUrl: string,
    options?: { skipPageRender?: boolean },
  ) => {
    busy = true;
    error = "";
    if (!options?.skipPageRender) {
      status = "กำลังบันทึกเฟรม…";
      render();
    }
    try {
      const dataUrl = await applySheetUploadClean(croppedDataUrl, getSheetUploadCleanPrefs());
      setCell(row, col, dataUrl);
      status = `ใส่เฟรม ${col + 1} · ${actionLabelForRow(row)} แล้ว`;
    } catch (e) {
      error = e instanceof Error ? e.message : "บันทึกไม่สำเร็จ";
      if (!options?.skipPageRender) status = "";
    } finally {
      busy = false;
      if (!options?.skipPageRender) render();
    }
  };

  const openCropForCell = async (row: number, col: number, file: File) => {
    error = "";
    status = "";
    render();
    try {
      const sourceDataUrl = await readImageFileAsDataUrl(file);
      closeCropModal?.();
      closeCropModal = openPetFrameCropModal({
        title: "ครอปจากภาพ · Snap ลงกริด",
        subtitle: `เริ่มที่ ${actionLabelForRow(row)} · ช่อง ${col + 1}`,
        sourceDataUrl,
        frameWidth: TEMPLATE_FRAME_WIDTH,
        frameHeight: TEMPLATE_FRAME_HEIGHT,
        slotPicker: {
          initialRow: row,
          initialCol: col,
          isFilled: (r, c) => Boolean(cells[r]?.[c]),
          thumb: (r, c) => cells[r]?.[c] ?? null,
          onSnap: async (r, c, dataUrl) => {
            await saveCroppedFrameToCell(r, c, dataUrl, { skipPageRender: true });
          },
        },
        onDone: () => {
          status = `กรอกแล้ว ${countFilledPetFrameCells(cells)} / ${GRID_ROWS * GRID_COLUMNS}`;
          render();
        },
        onCancel: () => {
          status = "";
          render();
        },
      });
    } catch (e) {
      error = e instanceof Error ? e.message : "เปิดไฟล์ไม่สำเร็จ";
      render();
    }
  };

  const buildComposedSheet = async (): Promise<string> => {
    const missing = missingPetFrameCells(cells);
    if (missing.length > 0) {
      throw new Error(`ยังไม่ครบ: ${missing.slice(0, 4).join(", ")}${missing.length > 4 ? "…" : ""}`);
    }
    return composePetFrameGrid(cells, TEMPLATE_FRAME_WIDTH, TEMPLATE_FRAME_HEIGHT);
  };

  const onChange = (event: Event) => {
    const input = event.target;
    if (input instanceof HTMLInputElement && input.id === "shadow-grid-full-sheet-file") {
      const file = input.files?.[0];
      input.value = "";
      if (file) void ingestFullSheetFile(file);
      return;
    }
    if (!(input instanceof HTMLInputElement) || !input.classList.contains("shadow-frame-file")) {
      return;
    }
    const label = input.closest<HTMLElement>("[data-frame-row]");
    if (!label) return;
    const row = Number(label.dataset.frameRow);
    const col = Number(label.dataset.frameCol);
    const file = input.files?.[0];
    input.value = "";
    if (!file || !Number.isFinite(row) || !Number.isFinite(col)) return;
    void openCropForCell(row, col, file);
  };

  const onClick = (event: Event) => {
    const target = event.target as HTMLElement;
    const clearBtn = target.closest<HTMLElement>("[data-frame-clear]");
    if (clearBtn?.dataset.frameClear) {
      const [rowStr, colStr] = clearBtn.dataset.frameClear.split("-");
      const row = Number(rowStr);
      const col = Number(colStr);
      if (Number.isFinite(row) && Number.isFinite(col)) {
        setCell(row, col, null);
        status = "";
        error = "";
        render();
      }
      return;
    }
    if (target.closest("#shadow-grid-auto-apply")) {
      void applyAutoSheetToGrid();
      return;
    }
    if (target.closest("#shadow-grid-auto-clear")) {
      clearAutoSheetPreview();
      status = "";
      error = "";
      render();
      return;
    }
    if (target.closest("#shadow-grid-clear-all")) {
      cells = emptyPetFrameGrid();
      previewUrl = null;
      status = "ล้างกริดแล้ว";
      error = "";
      render();
      return;
    }
    if (target.closest("#shadow-grid-close")) {
      window.close();
      return;
    }
    if (target.closest("#shadow-grid-preview")) {
      void (async () => {
        busy = true;
        error = "";
        status = "กำลังประกอบชีต…";
        render();
        try {
          previewUrl = await buildComposedSheet();
          status = "ตัวอย่างชีตด้านล่าง — กด «ใช้ชีตนี้และกลับ» เมื่อพร้อม";
        } catch (e) {
          error = e instanceof Error ? e.message : "ประกอบไม่สำเร็จ";
          status = "";
        } finally {
          busy = false;
          render();
        }
      })();
      return;
    }
    if (target.closest("[data-shadow-grid-create]")) {
      void submitCreatePet();
      return;
    }
    if (target.closest("#shadow-grid-apply")) {
      void (async () => {
        busy = true;
        error = "";
        status = "กำลังส่งชีตกลับ…";
        render();
        try {
          const dataUrl = await buildComposedSheet();
          savePetSheetGridResult(dataUrl);
          notifyPetSheetGridOpener();
          status =
            "ส่งชีตแล้ว — กลับไปแท็บสร้างสัตว์เลี้ยง ระบบจะโหลดชีตอัตโนมัติ (ปิดแท็บนี้ได้)";
          window.close();
        } catch (e) {
          error = e instanceof Error ? e.message : "ส่งไม่สำเร็จ";
          status = "";
        } finally {
          busy = false;
          render();
        }
      })();
    }
  };

  const submitCreatePet = async () => {
    if (!createMode || !petState || busy) return;
    const nameInput = root.querySelector<HTMLInputElement>("#shadow-grid-create-name");
    const name = (nameInput?.value ?? draftName).trim() || "สัตว์เลี้ยง";
    draftName = name;
    busy = true;
    error = "";
    status = "กำลังสร้างสัตว์เลี้ยง…";
    render();
    try {
      const total = GRID_ROWS * GRID_COLUMNS;
      if (countFilledPetFrameCells(cells) < total && autoSheetPreviewDataUrl) {
        status = "กำลังแยกเฟรมจากชีต…";
        cells = await extractPetFrameGridFromSheet(
          autoSheetPreviewDataUrl,
          autoGridTuning,
          getSheetUploadCleanPrefs(),
        );
        previewUrl = null;
      }
      const sheetUrl = await buildComposedSheet();
      const size = await loadImageSize(sheetUrl);
      const gridTuning = gridTuningForImageSize(
        size.width,
        size.height,
        supportsTemplateGridLayout,
      );
      const result = await appendPetFromSpritesheet(petState, economy, {
        name,
        sheetDataUrl: sheetUrl,
        gridTuning,
      });
      if (!result.ok) {
        error = result.reason;
        status = "";
        return;
      }
      petState = result.state;
      economy = result.economy;
      await savePetState(user.uid, petState);
      await saveHomeEconomyToIdb(user.uid, economy);
      await syncEquippedPetPublic(user.uid, equippedPet(petState));
      clearPetSheetGridCreateSession();
      notifyPetSheetGridCreated(result.pet.name);
      window.dispatchEvent(
        new CustomEvent(HomeEvents.PetChanged, {
          detail: { state: petState, equipped: equippedPet(petState) },
        }),
      );
      status = `สร้าง ${result.pet.name} แล้ว — ปิดแท็บหรือกลับไปแท็บเกม`;
      setTimeout(() => {
        window.close();
      }, 400);
    } catch (e) {
      error = e instanceof Error ? e.message : "สร้างไม่สำเร็จ";
      status = "";
    } finally {
      busy = false;
      render();
    }
  };

  const onInput = (event: Event) => {
    const input = event.target;
    if (input instanceof HTMLInputElement && input.id === "shadow-grid-create-name") {
      draftName = input.value;
    }
  };

  void (async () => {
    try {
      economy = (await loadHomeEconomyFromIdb(user.uid)) ?? createDefaultHomeEconomy();
      petState = await loadPetState(user.uid);
    } catch {
      error = "โหลดข้อมูลผู้เล่นไม่สำเร็จ";
    } finally {
      economyReady = true;
      render();
    }
  })();

  root.addEventListener("change", onChange);
  root.addEventListener("click", onClick);
  root.addEventListener("input", onInput);
  render();

  return () => {
    root.removeEventListener("change", onChange);
    root.removeEventListener("click", onClick);
    root.removeEventListener("input", onInput);
    unbindClean?.();
    closeCropModal?.();
    closeCropModal = undefined;
    stopAutoSheetPreview?.();
    stopAutoSheetPreview = undefined;
    root.replaceChildren();
  };
}
